import { useState } from 'react';
import { AsyncBoundary, Notice } from '@kollez/design-system';
import {
  useAnalyticsAgreements,
  type AnalyticsAgreementStatus,
} from '@/lib/queries';

/**
 * /platform/analytics-agreements (Page 79/N) — DPDP §7.2 cross-tenant
 * analytics consent register.
 *
 * Every partner that a tenant has authorised to run analytics against
 * their data. Platform ops watches this as regulator evidence and
 * catches expiring windows before they lapse.
 */

const STATUS_LABEL: Record<AnalyticsAgreementStatus, string> = {
  Draft: 'Draft',
  Signed: 'Signed',
  Active: 'Active',
  Expiring: 'Expiring',
  Expired: 'Expired',
  Terminated: 'Terminated',
  Renewed: 'Renewed',
};

function statusTone(s: AnalyticsAgreementStatus): string {
  if (s === 'Active') return 'badge-success';
  if (s === 'Expiring') return 'badge-warning';
  if (s === 'Expired' || s === 'Terminated') return 'badge-danger';
  if (s === 'Draft') return '';
  return 'badge-info';
}

function fmt(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function daysUntil(iso?: string): string {
  if (!iso) return '';
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.round((then - now) / (24 * 60 * 60 * 1000));
  if (days < 0) return `${-days}d ago`;
  if (days === 0) return 'today';
  return `in ${days}d`;
}

export function PlatformAnalyticsAgreementsPage() {
  const [status, setStatus] = useState<AnalyticsAgreementStatus | ''>('Active');
  const [expiring, setExpiring] = useState<'' | '30' | '60' | '90'>('');
  const [q, setQ] = useState('');

  const list = useAnalyticsAgreements({
    status: expiring ? '' : status,
    expiringInDays: expiring ? Number(expiring) : undefined,
    q,
    sortBy: 'term.endsAt',
    sortDir: 'asc',
    limit: 100,
  });
  const rows = list.data?.rows ?? [];
  const summary = list.data?.summary ?? {};

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Platform · DPDP §7.2</p>
          <h1 className="h-page">Analytics agreements</h1>
          <p className="subhead">
            Every cross-tenant analytics-consent agreement. Regulator evidence: who
            authorised whom, for what scope, and until when. Every read is audited as
            a cross-tenant access.
          </p>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <div className="card kpi"><span className="kpi-label">Active</span>
          <span className="kpi-value">{summary.Active ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Expiring</span>
          <span className="kpi-value">{summary.Expiring ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Signed</span>
          <span className="kpi-value">{summary.Signed ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Draft</span>
          <span className="kpi-value">{summary.Draft ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Expired</span>
          <span className="kpi-value">{summary.Expired ?? 0}</span></div>
      </div>

      <div className="card">
        <div className="filterbar">
          <input className="input" placeholder="Search agreement # or title" value={q} onChange={(e) => setQ(e.target.value)} />
          <select
            className="select"
            value={status}
            disabled={Boolean(expiring)}
            onChange={(e) => setStatus(e.target.value as AnalyticsAgreementStatus | '')}
          >
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[AnalyticsAgreementStatus, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="select" value={expiring} onChange={(e) => setExpiring(e.target.value as '' | '30' | '60' | '90')}>
            <option value="">Any expiry</option>
            <option value="30">Expiring in 30 days</option>
            <option value="60">Expiring in 60 days</option>
            <option value="90">Expiring in 90 days</option>
          </select>
          <span className="fb-count">{list.data?.meta?.total ?? rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No agreements in scope"
          emptyHint="Widen the filters, or wait for the first partner to sign — analytics access requires a written agreement per DPDP §7.2."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Partner → Tenant</th>
                  <th>Scope</th>
                  <th>Ends</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} style={{ cursor: 'pointer' }} onClick={() => { window.location.href = `/platform/analytics-agreements/${r._id}`; }}>
                    <td className="cell-sub">{r.agreementNumber ?? '—'}</td>
                    <td>
                      <div className="cell-title">
                        {typeof r.partner === 'object' && r.partner?.name ? r.partner.name : '—'}
                        <span className="cell-sub"> → </span>
                        {typeof r.tenant === 'object' && (r.tenant?.displayName ?? r.tenant?.slug) || '—'}
                      </div>
                      {r.title && <div className="cell-sub">{r.title}</div>}
                    </td>
                    <td className="cell-sub">
                      {r.scope && r.scope.length > 0 ? r.scope.slice(0, 3).join(', ') + (r.scope.length > 3 ? ' …' : '') : '—'}
                    </td>
                    <td className="cell-sub">
                      {fmt(r.term?.endsAt)}
                      {r.term?.endsAt && <div className="cell-sub">{daysUntil(r.term.endsAt)}</div>}
                    </td>
                    <td><span className={`badge ${statusTone(r.status)}`}>{STATUS_LABEL[r.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="warning">
        <b>60-day rule.</b> Watch the "Expiring in 60 days" bucket. Partners running
        analytics after a lapsed agreement is a DPDP §7.2 breach — cut access before,
        not after.
      </Notice>
    </div>
  );
}
